import {observable, action} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import uris from '~/uris/index';
import axiosInstance from '~/utils/axiosInstance';
import MessageApps from './models/MessageApps';
import modalAction from '~/utils/modalStack';
import {LATEST_MESSAGE_TIME_STAMP_SESSION_KEY, USER_INFO_STORAGE_KEY, DISABLE_SOUND_SESSION_KEY, DISABLE_DIALOG_SESSION_KEY, ALARM_MESSAGE_LENGTH} from '~/utils/constants';

const {pushStack, popStack} = modalAction;

class MessageStore {
  autoShow = true;

  @observable apps = [];

  @observable messagesNumber = 0; // 实时告警总数

  @observable soundLoop = 0; // 告警铃声循环次数

  @observable currentApp = {};

  @observable loading = false; // 是否加载中

  @observable isLoadFailed = false; // 是否加载超时

  @observable showMessage = false; // 是否显示消息窗口

  @observable markSoundStatus = 0;

  @observable disableSound = localStorage.getItem(this.getSoundSessionKey()) !== 'false'; // 铃声是否关闭

  @observable disableDialog = localStorage.getItem(this.getDialogSessionKey()) !== 'false'; // 是否关闭主动弹窗

  messageTimer;

  @observable ring = [];

  @action.bound
  setApps(data) {
    this.apps = data;
    if (_.isPlainObject(this.currentApp)) {
      this.setCurrentApp(this.apps[0]);
    } else {
      const currentApp = this.apps.find(({id}) => id === (this.currentApp || {}).id); // 该app没有并移除的情况下
      if (!currentApp) {
        this.setCurrentApp(this.apps[0]);
      }
    }
  }

  @action.bound
  setCurrentApp(data) {
    this.currentApp = data;
  }

  @action.bound
  setLoading(boolean) {
    this.loading = boolean;
  }

  @action.bound
  setLoadFailed(boolean) {
    this.isLoadFailed = boolean;
  }

  @action.bound
  setSoundLoop(times) {
    this.soundLoop = times;
  }

  @action.bound
  setRemoveLoading(boolean) {
    this.removeLoading = boolean;
  }

  @action.bound
  getNotification(data) {
    if (data?.apps && !data?.activeOrNot) { // 后端主动推送
      this.setLoading(false);
      this.setLoadFailed(false);
      this.setApps((data.apps || []).map(json => this.updateAppsFromServer(json)));
      this.handleInit(data);
    }
    if (data?.apps && data?.activeOrNot) { // 前端点击加载更多获取
      this.apps.forEach((app) => {
        if (app.id === data.apps?.[0]?.id) {
          if (data.apps?.[0]?.count === 0) { // 若该app中message数量为0，移除该app
            _.remove(this.apps, item => item.id === data.apps?.[0]?.id);
          } else {
            app.messages = data.apps[0].messages; // 替换应用中的messages
            app.count = data.apps[0].count; // 替换应用count
          }
        }
      });
      this.setApps((this.apps || []).map(json => this.updateAppsFromServer(json)));
      this.setLoading(false);
      this.setLoadFailed(false);
    }
    if (data?.updateMessages) { // 移除message
      data.updateMessages.forEach((app) => {
        app.messages.forEach((message) => {
          this.removeMessageFromSever(message.messageId, app);
        });
      });
    }
    if (data?.createMessages) { // createMessage
      data.createMessages.forEach((app) => {
        this.createMessagesFromServer(app);
      });
    }
    if (data?.code === '500') { // 异常情况
      this.setLoadFailed(true);
    }
  }

  @action.bound
  updateAppsFromServer(json) {
    let app = this.apps.find(({id}) => id === json.id);
    if (!app) {
      app = new MessageApps(this, json);
    }
    app.updateFromJson(json);
    return app;
  }

  @action.bound
  setSoundAndDialog(count) {
    if (count > 0 && !this.disableSound && this.ring.includes(1)) {
      this.markSoundStatus++;
      this.soundLoop = count;
    }

    if (count > 0 && !this.disableDialog && this.autoShow) {
      this.showMessage = true;
    }
  }

  @action.bound
  handleInit(data) {
    this.messagesNumber = data.total;
    if (data.total <= 0) {
      return false;
    }
    this.setSoundAndDialog(this.getNewlyCount(data));

    return true;
  }

  @action.bound
  toggleMessagePoPUp(boolean) {
    this.showMessage = boolean;
    try {
      document.getElementsByClassName('message-list')[0].scrollTop = 0;
    } catch (e) {
      console.log(e);
    }
    if (boolean) {
      pushStack(() => this.toggleMessagePoPUp(false));
    } else {
      popStack();
    }
  }

  @action.bound
  toggleSound(boolean) {
    localStorage.setItem(this.getSoundSessionKey(), boolean);
    this.disableSound = boolean;
  }

  @action.bound
  toggleAutoPopUp(boolean) {
    localStorage.setItem(this.getDialogSessionKey(), boolean);
    this.disableDialog = boolean;
  }

  // 由于后端返回的数据存在两种，首次加载或刷新后返回的是{apps: [app, app, ...]}
  // createMessages返回的不包含外层的apps，直接返回某个app，结构是{id: xxx, count: xxx, messages: [message, message, ...]}
  // 因此增加状态位isFirstLoad标志是否是刷新加载，默认是true

  @action.bound
  setRing(num) {
    this.ring.push(num);
  }

  getNewlyCount(data, isRefreshLoad = true) {
    const timeStamp = sessionStorage.getItem(this.getTimeStampSessionKey()) || 0;
    let newStamp = timeStamp;
    let count = 0;
    this.ring = [];

    if (isRefreshLoad) {
      (data.apps || []).forEach(app => (app.messages || []).forEach((m) => {
        const currentStamp = moment(m.messageTime)
          .utc()
          .valueOf();

        if (currentStamp > newStamp) {
          newStamp = currentStamp;
        }

        if (currentStamp > timeStamp) {
          count += 1;
          this.setRing(m.ring);
        }
      })
      );
    } else {
      (data.messages || []).forEach((m) => {
        const currentStamp = moment(m.messageTime)
          .utc()
          .valueOf();

        if (currentStamp > newStamp) {
          newStamp = currentStamp;
        }

        if (currentStamp > timeStamp) {
          count += 1;
          this.setRing(m.ring);
        }
      });
    }

    sessionStorage.setItem(this.getTimeStampSessionKey(), newStamp);
    return count;
  }

  getSoundSessionKey() {
    return `${DISABLE_SOUND_SESSION_KEY}_${this.getCurrentUserId()}`; // eg: disabled_sound_u15589256596201
  }

  getDialogSessionKey() {
    return `${DISABLE_DIALOG_SESSION_KEY}_${this.getCurrentUserId()}`;
  }

  getTimeStampSessionKey() {
    return `${LATEST_MESSAGE_TIME_STAMP_SESSION_KEY}_${this.getCurrentUserId()}`;
  }

  getCurrentUserId() {
    try {
      return JSON.parse(localStorage.portalGetItem(USER_INFO_STORAGE_KEY)).id;
    } catch {
      return {}.id;
    }
  }

  @action.bound
  setMessagesNumber(count) {
    this.messagesNumber = count;
  }

  setAutoShow(boolean) {
    this.autoShow = boolean;
  }

  @action.bound
  removeMessage(messageId, appId, env) {
    axiosInstance
      .post(uris.removeMessage(), {
        id: messageId,
        appId
      })
      .then(() => {
        if (env === 'edge') {
          this.getMessageList();
        }
      });
  }

  /**
    * updateMessages，移除告警, 单个应用或多个应用的单条或多条告警
    * @param {*} messageId 移除告警的id
    * @param {*} appItem 该告警所属的app
    * 数据格式
    * [{
    *   id, 应用id
    *   count, 该应用下告警总数目
    *   messages: [{ 告警列表
    *     appId,
    *     messageId,
    *     state
    *   }, {}, ...]
    * }, {}, ...]
    */
  @action.bound
  removeMessageFromSever(messageId, appItem) {
    this.apps.forEach((app) => {
      if (app.id === appItem.id) {
        app.messages.forEach((message) => {
          if (message.id === messageId) {
            this.setMessagesNumber(this.messagesNumber - app.count + appItem.count);
            app.count = appItem.count;
          }
        });
        _.remove(app.messages, item => item.id === messageId); // 移除告警
        if (app.count === 0) { // 若该app中message数量为0，移除该app
          _.remove(this.apps, item => item.id === app.id);
        }
      }
    });
    this.setApps((this.apps || []).map(json => this.updateAppsFromServer(json)));
  }

  /**
   * createMessages 新增告警，单个应用或多个应用的单条或多条告警
   * @param {*} appItem 新增告警信息
   * * 数据格式
    * [{
    *   id, 应用id
    *   name: {default: string}, 应用名字
    *   count, 该应用下告警总数目
    *   messages: [{ 告警列表
    *     appId,
    *     messageId,
    *     state
    *   }, {}, ...]
    * }, {}, ...]
  */
  @action.bound
  createMessagesFromServer(appItem) {
    const appIds = this.apps.map(app => app.id);
    if (appIds?.includes(appItem.id)) { // 已存在的app中添加告警
      this.apps.forEach((app) => {
        // app.time = moment(new Date(1997, 1, 1)).unix();
        if (app.id === appItem.id) {
          const count = this.getNewlyCount(appItem, false);
          (appItem?.messages || []).forEach((message) => {
            if (app.messages?.length >= ALARM_MESSAGE_LENGTH) { // 若message数量大于等于50，删除最后一条message
              app.messages.pop();
            }
            this.setMessagesNumber(this.messagesNumber - app?.count + appItem.count);
            app.messages.unshift(message); // 将数据添加到列表前面
            app.count = appItem.count;
            this.setSoundAndDialog(count);
          });
        }
      });
    } else { // 未存在的app，直接将app加入到app列表中
      this.apps.unshift(this.updateAppsFromServer(appItem));
      this.setMessagesNumber(this.messagesNumber + appItem?.count);
      this.setSoundAndDialog(this.getNewlyCount(appItem, false));
    }
    // const newApp = this.apps.sort((a, b) => b.time - a.time); // 排序后的app
    // this.setApps((newApp || []).map(json => this.updateAppsFromServer(json)));
    this.setApps((this.apps || []).map(json => this.updateAppsFromServer(json)));
  }

  loopMessage(frequency) {
    this.getMessageList();
    this.messageTimer = setInterval(async() => {
      await this.getMessageList();
    }, frequency * 1000);
  }

  getMessageList() {
    return axiosInstance
      .get(uris.messageList(), {showError: {isShow: false}})
      .then((data) => {
        this.getNotification(data);
      });
  }

  clearMessageTimer() {
    clearInterval(this.messageTimer);
  }
}
export default new MessageStore();
